import React, { useEffect, useState } from "react";
import { MonoButton } from "../kits";
import { Link } from "react-router-dom";

function SkillManager() {
  const [skills, setSkills] = useState([]);
  const [expandedId, setExpandedId] = useState(null);
  const [newSkill, setNewSkill] = useState({
    name: "",
    description: "",
    skillExample: "",
    mediaUrl: "",
    mediaType: "image",
  });
  const [isLoading, setIsLoading] = useState(false);

  const extractYouTubeId = (url) => {
    const match = url.match(
      /^(?:https?:\/\/)?(?:www\.)?(?:youtube\.com\/(?:[^\/\n\s]+\/\S+\/|(?:v|e(?:mbed)?|shorts)\/|.*[?&]v=)|youtu\.be\/)([a-zA-Z0-9_-]{11})/
    );
    return match ? match[1] : "";
  };

  // Auto-Detect mediaType based on URL
  const autoDetectType = (url) => {
    if (/youtu\.?be|youtube\.com/.test(url)) return "youtube";
    if (url.match(/\.(mp4|webm|ogg)$/i)) return "video";
    if (url.match(/\.(jpe?g|png|gif|svg|webp)$/i)) return "image";
    return "image";
  };

  useEffect(() => {
    fetch("https://www.denisbitter.de/stage01/api/get_all_skills.php")
      .then((res) => res.json())
      .then((data) => {
        if (data.success && data.data) setSkills(data.data);
      });
  }, []);

  const toggleExpand = (id) => {
    setExpandedId((prev) => (prev === id ? null : id));
  };

  const handleSkillChange = (id, field, value) => {
    setSkills((prev) =>
      prev.map((s) =>
        s.id === id
          ? field === "mediaUrl"
            ? {
                ...s,
                mediaUrl: value,
                mediaType: autoDetectType(value),
              }
            : { ...s, [field]: value }
          : s
      )
    );
  };

  const handleImageDropForSkill = async (e, skillId = null) => {
    e.preventDefault();
    const file = e.dataTransfer.files[0];
    if (!file || !file.type.startsWith("image/")) return;

    const formData = new FormData();
    formData.append("file", file);

    const res = await fetch("https://www.denisbitter.de/stage01/api/upload_image.php", {
      method: "POST",
      body: formData,
    });

    let result;
    try {
      const text = await res.text();
      console.log("Upload response (raw):", text);
      result = JSON.parse(text);
    } catch (err) {
      console.error("Fehler beim Parsen der Antwort:", err);
      alert("Upload fehlgeschlagen: Ungültige Antwort vom Server.");
      return;
    }

    if (result.success) {
      if (skillId) {
        handleSkillChange(skillId, "mediaUrl", result.url);
      } else {
        setNewSkill((prev) => ({
          ...prev,
          mediaUrl: result.url,
          mediaType: "image",
        }));
      }
    } else {
      alert("Upload fehlgeschlagen: " + (result.message || "Unbekannter Fehler"));
    }
  };

  const saveSkill = async (skill) => {
    setIsLoading(true);
    // Safety: Validate mediaType before saving
    let finalSkill = { ...skill };
    if (skill.mediaUrl) {
      finalSkill.mediaType = autoDetectType(skill.mediaUrl);
    }
    const res = await fetch("https://www.denisbitter.de/stage01/api/update_skill.php", {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(finalSkill),
    });
    const result = await res.json();
    setIsLoading(false);
    alert(result.message || "Skill aktualisiert");
  };

  const deleteSkill = async (id) => {
    if (!window.confirm("Diesen Skill wirklich löschen?")) return;
    setIsLoading(true);
    await fetch("https://www.denisbitter.de/stage01/api/delete_skill.php", {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ id }),
    });
    setSkills((prev) => prev.filter((s) => s.id !== id));
    setIsLoading(false);
  };

  const addSkill = async () => {
    if (!newSkill.name.trim()) return;
    setIsLoading(true);
    // Auto-detect type before send
    const skillToAdd = {
      ...newSkill,
      mediaType: autoDetectType(newSkill.mediaUrl),
    };
    const res = await fetch("https://www.denisbitter.de/stage01/api/add_skill.php", {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(skillToAdd),
    });
    const result = await res.json();
    if (result.success) {
      setSkills((prev) => [...prev, result.skill]);
      setNewSkill({
        name: "",
        description: "",
        skillExample: "",
        mediaUrl: "",
        mediaType: "image",
      });
    }
    setIsLoading(false);
  };

  return (
    <div className="max-w-3xl mx-auto space-y-4">
      <h2 className="text-xl font-bold">🧠 Skill Verwaltung</h2>

      {skills.map((skill) => (
        <div key={skill.id} className="border rounded p-4 bg-white">
          <div
            className="flex justify-between items-center cursor-pointer"
            onClick={() => toggleExpand(skill.id)}
          >
            <h3 className="font-semibold">{skill.name}</h3>
            <span>{expandedId === skill.id ? "▴" : "▾"}</span>
          </div>
          {expandedId === skill.id && (
            <div className="mt-4 space-y-2">
              <input
                className="w-full border px-2 py-1 rounded"
                value={skill.name}
                onChange={(e) => handleSkillChange(skill.id, "name", e.target.value)}
                placeholder="Skill Name"
              />
              <input
                className="w-full border px-2 py-1 rounded"
                value={skill.description}
                onChange={(e) => handleSkillChange(skill.id, "description", e.target.value)}
                placeholder="Beschreibung"
              />
              <textarea
                className="w-full border px-2 py-1 rounded"
                value={skill.skillExample}
                onChange={(e) => handleSkillChange(skill.id, "skillExample", e.target.value)}
                placeholder="Beispiel"
              />
              <input
                className="w-full border px-2 py-1 rounded"
                value={skill.mediaUrl || ""}
                onChange={(e) => handleSkillChange(skill.id, "mediaUrl", e.target.value)}
                placeholder="Media URL"
              />
              <div
                onDrop={(e) => handleImageDropForSkill(e, skill.id)}
                onDragOver={(e) => e.preventDefault()}
                className="border-dashed border-2 border-gray-400 p-4 rounded mb-2 text-center"
              >
                📤 Ziehe ein Bild hierher, um es hochzuladen
              </div>
              <select
                className="w-full border px-2 py-1 rounded"
                value={skill.mediaType || "image"}
                onChange={(e) => handleSkillChange(skill.id, "mediaType", e.target.value)}
              >
                <option value="image">🖼️ Bild</option>
                <option value="video">🎮 Video</option>
                <option value="youtube">▶️ YouTube</option>
              </select>

              {skill.mediaType === "image" && skill.mediaUrl && (
                <img src={skill.mediaUrl} className="w-full max-h-40 object-contain" alt="Preview" />
              )}
              {skill.mediaType === "video" && skill.mediaUrl && (
                <video controls className="w-full max-h-40 rounded">
                  <source src={skill.mediaUrl} type="video/mp4" />
                </video>
              )}
              {skill.mediaType === "youtube" && skill.mediaUrl && (
                <iframe
                  src={`https://www.youtube.com/embed/${extractYouTubeId(skill.mediaUrl)}`}
                  className="w-full h-64 rounded"
                  allowFullScreen
                  frameBorder="0"
                  allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                  title="YouTube Preview"
                />
              )}

              <div className="flex gap-2">
                <MonoButton onClick={() => saveSkill(skill)}>💾 Speichern</MonoButton>
                <MonoButton onClick={() => deleteSkill(skill.id)} className="bg-red-100 text-red-600">
                  🗑️ Löschen
                </MonoButton>
              </div>
            </div>
          )}
        </div>
      ))}

      <div className="border rounded p-4 bg-gray-50">
        <h3 className="font-semibold mb-2">➕ Neuer Skill</h3>
        <input
          className="w-full border px-2 py-1 rounded mb-2"
          placeholder="Name"
          value={newSkill.name}
          onChange={(e) => setNewSkill((p) => ({ ...p, name: e.target.value }))}
        />
        <textarea
          className="w-full border px-2 py-1 rounded mb-2"
          placeholder="Beschreibung"
          value={newSkill.description}
          onChange={(e) => setNewSkill((p) => ({ ...p, description: e.target.value }))}
        />
        <input
          className="w-full border px-2 py-1 rounded mb-2"
          placeholder="Beispiel"
          value={newSkill.skillExample}
          onChange={(e) => setNewSkill((p) => ({ ...p, skillExample: e.target.value }))}
        />
        <input
          className="w-full border px-2 py-1 rounded mb-2"
          placeholder="Media URL"
          value={newSkill.mediaUrl}
          onChange={(e) => {
            const val = e.target.value;
            setNewSkill((prev) => ({
              ...prev,
              mediaUrl: val,
              mediaType: autoDetectType(val),
            }));
          }}
        />
        <div
          onDrop={(e) => handleImageDropForSkill(e, null)}
          onDragOver={(e) => e.preventDefault()}
          className="border-dashed border-2 border-gray-400 p-4 rounded mb-2 text-center"
        >
          📤 Ziehe ein Bild hierher, um es hochzuladen
        </div>
        <select
          className="w-full border px-2 py-1 rounded mb-2"
          value={newSkill.mediaType}
          onChange={(e) => setNewSkill((p) => ({ ...p, mediaType: e.target.value }))}
        >
          <option value="image">🖼️ Bild</option>
          <option value="video">🎮 Video</option>
          <option value="youtube">▶️ YouTube</option>
        </select>

        {newSkill.mediaType === "image" && newSkill.mediaUrl && (
          <img src={newSkill.mediaUrl} className="w-full max-h-40 object-contain" alt="Preview" />
        )}
        {newSkill.mediaType === "video" && newSkill.mediaUrl && (
          <video controls className="w-full max-h-40 rounded">
            <source src={newSkill.mediaUrl} type="video/mp4" />
          </video>
        )}
        {newSkill.mediaType === "youtube" && newSkill.mediaUrl && (
          <iframe
            src={`https://www.youtube.com/embed/${extractYouTubeId(newSkill.mediaUrl)}`}
            className="w-full h-64 rounded"
            allowFullScreen
            frameBorder="0"
            allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
            title="YouTube Preview"
          />
        )}

        <MonoButton className="text-xs" onClick={addSkill} disabled={isLoading}>
          Auf Server Speichern
        </MonoButton>
        <Link to="/admindashboard">
          <MonoButton className="text-xs">Admin Dashboard</MonoButton>
        </Link>
      </div>
    </div>
  );
}

export default SkillManager;
